import CookieConsentApi from "./cookie-consent-api.js";

/**
 * Add data-adobe-eventname="cta|interaction|download" to all focusable/clickable elements
 * cta = yellow cta "button"s
 * download = pdf download links
 * interaction = everything else
 *
 * data-adobe-eventcategory="application|follow-through|anchor|contact"
 *
 * data-adobe-eventposition="header|footer|@todo" <- not used/defined by sales properly currently
 *
 * We avoid detecting/scraping with js so CSS/styling/JS changes, for example changing css
 *  class doesn't break tracking, we use data attributes instead
 *
 * https://confluence-general.sebank.se/display/SKITF/OWe+-+Eurocard+XX#OWeEurocardXX-Tracking
 */

const config = {
  global: {
    trackAccordions: false,
    trackCta: false,
    trackInteraction: false,
    trackDownload: false,
  },
  eurocard: {
    trackAccordions: window.TrackAccordions,
    trackCta: true,
    trackInteraction: true,
    trackDownload: true,
  },
  spendwise: {
    trackAccordions: true,
    trackCta: window.TrackCta,
    trackInteraction: window.TrackInteraction,
    trackDownload: window.TrackDownload,
  },
  sas: {
    trackAccordions: false,
    trackCta: window.TrackCta,
    trackInteraction: window.TrackInteraction,
    trackDownload: window.TrackDownload,
  },
};
const getConfig = (key) => {
  const brand = window.dataLayer?.brand?.toLowerCase() ?? "global";
  if (config[brand] === undefined) {
    return config.global[key];
  }
  if (config[brand][key] === undefined) {
    return config.global[key];
  }
  return config[brand][key];
};

const clickableElsString = `
    a[href]:not([tabindex='-1']),
    area[href]:not([tabindex='-1']),
    input:not([disabled]):not([tabindex='-1']):not([type="hidden"]),
    select:not([disabled]):not([tabindex='-1']),
    textarea:not([disabled]):not([tabindex='-1']),
    button:not([disabled]):not([tabindex='-1']),
    [tabindex]:not([tabindex='-1']),
    [contentEditable=true]:not([tabindex='-1']),
    summary:not([tabindex='-1'])
`;

const ignoredElementCSSQuery = `
    a:not(.button),
    input,
    [href*="/EPiServer"],
    .epi-quickNavigator-dropdown-arrow,
    [href*="license.episerver.com"],
    .external-content-output a,
    .external-content-output input,
    .external-content-output textarea,
    .external-content-output button,
    .external-content-output [tabindex],
    .external-content-output [contentEditable=true],
    .external-content-output select,
    .cookie-consent-settings-page summary
`;
const ignoredRuntimeCSSQuery = `
    details[open] summary
`;

const noWarnElementsCSSQuery = `
    .wysiwyg-output a,
    .wysiwyg-output button,
    .EPiServerForms input,
    .EPiServerForms textarea,
    .EPiServerForms button,
    .EPiServerForms select,
    .EPiServerForms a
`;

const formInputElements = ["input", "button", "select", "textarea"];

const isLocalHost = window.location.host.indexOf("localhost") !== -1;

window.EnableNewTracking = true;

class Tracking {
  constructor() {
    if (!window.EnableNewTracking) {
      return;
    }
    if (window.location.search.indexOf("debug_tracking=1") !== -1) {
      localStorage.setItem("debug_tracking", "1");
    }
    if (window.location.search.indexOf("debug_tracking=0") !== -1) {
      localStorage.removeItem("debug_tracking");
    }
    this.debug = localStorage.getItem("debug_tracking") !== null;
    if (this.debug && isLocalHost && !window.TrackingEnabled) {
      try {
        // only when tracking is async
        window.onLoadDtm();
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }

    const cookieConsentData = CookieConsentApi.get();
    const hasAnalyticalCookieConsent = cookieConsentData[
      "cookies-groups"
    ].filter((group) => group.name === "analyticalcookies")[0].approved;
    if (!hasAnalyticalCookieConsent) {
      return;
    }

    this.addClickEventListners();
    this.watchForJSRenderedElements();
  }

  watchForJSRenderedElements() {
    const config = {
      attributes: false,
      characterData: true,
      subtree: true,
      childList: true,
    };

    const targetNodes = document.querySelectorAll(
      "[data-react-component], blanco-application-root",
    );
    targetNodes.forEach((targetNode) => {
      let debounceTimer;
      const callback = (mutationList) => {
        mutationList.forEach(() => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.addClickEventListners();
          }, 500);
        });
      };
      const observer = new MutationObserver(callback.bind(this));
      observer.observe(targetNode, config);
    });
  }

  getEventName(target, opt = { useFallback: true }) {
    const fileExtensions = ["pdf"];

    const getFileExtension = (str) => {
      return str.split(".").pop().toLowerCase();
    };

    let eventName = target.dataset.adobeEventname;
    if (
      typeof target.href !== "undefined" &&
      fileExtensions.includes(getFileExtension(target.href))
    ) {
      eventName = "download";
    }
    if (!eventName && opt.useFallback) {
      // fallback
      if (
        target.classList.contains("button") ||
        target.type.toLowerCase() === "submit"
      ) {
        eventName = "cta";
      } else {
        eventName = "interaction"; // fallback
      }
    }
    return eventName;
  }
  getEventCategory(target) {
    const eventcategory = "";
    if (target.dataset.adobeEventcategory) {
      return target.dataset.adobeEventcategory;
    }
    const linkContainsHashChar = target.href && target.href.indexOf("#") !== -1;
    if (linkContainsHashChar) {
      if (target.href.indexOf("#") === 0) {
        return "anchor";
      }
      if (target.href.indexOf(window.location.pathname) !== -1) {
        return "anchor";
      }
    }
    return eventcategory;
  }
  getTextLabel(origTarget) {
    const target =
      formInputElements.includes(origTarget.nodeName.toLowerCase()) &&
      origTarget.labels.length
        ? origTarget.labels[0]
        : origTarget;

    let result = "";
    if (target.ariaLabel) {
      result = target.ariaLabel;
    } else if (target.innerText.trim().length) {
      result = target.innerText;
    } else if (target.alt) {
      result = target.alt;
    } else if (target.title) {
      result = target.title;
    }
    if (!result.length) {
      target.querySelectorAll("img").forEach((img) => {
        result += `${this.getTextLabel(img)} `;
      });
      target.querySelectorAll("svg title").forEach((img) => {
        result += `${this.getTextLabel(img)} `;
      });
    }
    return result
      .replace(/\n\n/g, "\n")
      .replace(/\n/g, "\\n")
      .replace(/\s\s/g, " ")
      .trim("\n");
  }

  getEventPosition(target) {
    let eventPosition = target.dataset.adobeEventposition;
    if (!eventPosition) {
      return "";
    }
    return eventPosition;
  }

  getEvent(eventName) {
    return eventName.split(" ")[0];
  }

  onClick(e) {
    const target = e.currentTarget;

    if (
      target.matches(ignoredElementCSSQuery) ||
      target.matches(ignoredRuntimeCSSQuery)
    ) {
      return;
    }

    // ignoring WYSIWYG elements where Editor hasn't defined this because tracking costs a lot (CJs descision)
    const eventName = this.getEventName(target, { useFallback: false });
    if (!eventName) {
      return;
    }
    const event = this.getEvent(eventName);
    if (event === "accordionlist" && !getConfig("trackAccordions")) {
      return;
    }
    if (event === "cta" && !getConfig("trackCta")) {
      return;
    }
    if (event === "interaction" && !getConfig("trackInteraction")) {
      return;
    }
    if (event === "download" && !getConfig("trackDownload")) {
      return;
    }

    const eventCategory = this.getEventCategory(target);
    const eventAction = "click";
    const eventLabel = this.getTextLabel(target);
    const eventPosition = this.getEventPosition(target);

    const otherProps = {};
    if (
      eventName.indexOf("accordionlist") !== -1 &&
      eventName.indexOf("faq") !== -1
    ) {
      otherProps.eventAccordionlistHeader = `faq|click|${eventLabel}|${eventPosition}`;
    }

    if (!window._satellite || typeof window._satellite.track !== "function") {
      return;
    }
    const doTracking = () => {
      window._satellite.track(event, {
        eventName,
        eventCategory,
        eventAction,
        eventLabel,
        eventPosition,
        ...otherProps,
      });
      if (this.debug) {
        console.log(`
                window._satellite.track('${event}', {
                        eventName: '${eventName}',
                        eventCategory: '${eventCategory}',
                        eventAction: '${eventAction}',
                        eventLabel: '${eventLabel}',
                        eventPosition: '${eventPosition}',
                        ${otherProps.eventAccordionlistHeader ? "eventAccordionlistHeader" : ""}: ${otherProps.eventAccordionlistHeader ? `'${otherProps.eventAccordionlistHeader}'` : ""}
                    });
                `);
      }
    };
    // https://stackoverflow.com/a/58026592/846348
    // We don't need to wait for any answer from server as long as we sent the request
    // window._satellite.track does not have any callback or promise though so we need to wait a random time
    let waitForTrackingBeforeFollowingLinks = (cb) => {
      const clonedNativeEvent = new MouseEvent("click", e);
      if (this.fakeClick) {
        this.fakeClick = false;
        return;
      }
      e.preventDefault();

      cb();

      this.fakeClick = true;
      if (this.debug) {
        // eslint-disable-next-line no-debugger
        debugger;
      }
      setTimeout(() => {
        target.dispatchEvent(clonedNativeEvent);
      }, 300);
    };
    waitForTrackingBeforeFollowingLinks =
      waitForTrackingBeforeFollowingLinks.bind(this);
    try {
      const hrefNonAnchor = target.href?.replace(
        window.location.href.split("#")[0],
        "",
      );
      const isCurrentPageAnchor =
        target.href && hrefNonAnchor.charAt(0) === "#";
      const isRegularLink = target.nodeName === "A" && !isCurrentPageAnchor;
      const isSubmitButton =
        target.nodeName === "BUTTON" && target.type.toLowerCase() === "submit";
      if (isRegularLink || isSubmitButton) {
        waitForTrackingBeforeFollowingLinks(doTracking);
      } else {
        doTracking();
      }
    } catch (e) {
      console.warn("Tracking failed");
    }
  }

  addClickEventListners() {
    const focusableElements = document.querySelectorAll(clickableElsString);
    focusableElements.forEach((el) => {
      if (!el.matches(ignoredElementCSSQuery)) {
        const fn = this.onClick.bind(this);
        if (!el.adobeClickFn) {
          if (!el.dataset.adobeEventname && (isLocalHost || this.debug)) {
            // && init
            if (
              !noWarnElementsCSSQuery.trim().length ||
              !el.matches(noWarnElementsCSSQuery)
            ) {
              console.warn(
                'For tracking please define "data-adobe-eventname" what type of element this is; "cta", "interaction" or "download" or add a wrapper class "wysiwyg-output" or "external-content-output" as needed or add it to noWarnElementsCSSQuery, see attached element',
                el,
              );
            }
          }
          el.addEventListener("click", fn, true);
          el.adobeClickFn = fn;
        }
      }
    });
  }
}

const trackingInit = () => {
  new Tracking();
};

export default trackingInit;
